import React, { useEffect, useState } from 'react'

import { useParams, useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import { Box, Button } from '@material-ui/core'
import { HelpCircle as HelpCircleIcon } from 'react-feather'

import { ContentHeader, Page } from 'components'
import {
  EvidencesSkeleton,
  QuestionSkeleton,
  QuizQuestionTemplateForm,
  QuestionTutorialDialog,
} from '../components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import { routes } from 'SupplierRoutes'
import * as service from 'service'
import helpers from 'helpers'

const QuizQuestionTemplateNew = () => {
  const [openTutorialDialog, setOpenTutorialDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { companySupplierQuestionnaireId, quizQuestionTemplateId } = useParams()
  const snackbar = useSnackbar()
  const history = useHistory()

  const { response, isLoading } = useFetch(
    service.supplierPortal.quizQuestionTemplates.get,
    { companySupplierQuestionnaireId, id: quizQuestionTemplateId },
    [quizQuestionTemplateId],
  )

  const {
    response: questionnairesResponse,
    isLoading: isLoadingQuestionnaires,
  } = useFetch(service.supplierPortal.companySupplierQuestionnaires.get, {})

  const quizQuestionTemplate = response?.data?.quizQuestionTemplate

  const companySupplierQuestionnaires =
    questionnairesResponse?.data?.companySupplierQuestionnaires

  useEffect(() => {
    if (quizQuestionTemplate?.answer) {
      history.replace(
        reverse(routes.quizQuestionTemplate.edit, {
          companySupplierQuestionnaireId,
          quizQuestionTemplateId,
        }),
      )
    }
  }, [
    quizQuestionTemplate,
    companySupplierQuestionnaireId,
    quizQuestionTemplateId,
    history,
  ])

  const { previousQuestion, nextQuestion } =
    helpers.suppliers.companySuppliers.getSiblingQuestions(
      companySupplierQuestionnaires,
      companySupplierQuestionnaireId,
      quizQuestionTemplateId,
    )

  const handleOpenTutorialDialog = () => setOpenTutorialDialog(true)
  const handleCloseTutorialDialog = () => setOpenTutorialDialog(false)

  const redirectToHome = () => history.push(routes.home)

  const navigateToQuestion = (targetQuestion) => {
    const routeKey = targetQuestion.hasAnswer ? 'edit' : 'new'
    history.push(
      reverse(routes.quizQuestionTemplate[routeKey], {
        companySupplierQuestionnaireId,
        quizQuestionTemplateId: targetQuestion.id,
      }),
    )
  }

  const onSubmit = async (data, targetQuestion) => {
    setIsSubmitting(true)
    try {
      await service.supplierPortal.questionnaireAlternativeAnswers.create({
        companySupplierQuestionnaireId,
        quizQuestionTemplateId,
        params: data,
      })

      snackbar.open({
        message: 'Resposta enviada com sucesso',
        variant: 'success',
      })

      if (targetQuestion) {
        navigateToQuestion(targetQuestion)
      } else {
        redirectToHome()
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar a resposta da questão.',
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const shouldRedirect = !!quizQuestionTemplate?.answer
  const isPageLoading = isLoading || isLoadingQuestionnaires || shouldRedirect

  return (
    <Page title="Questão de Formulário de Fornecedor">
      <Box px={2}>
        {isPageLoading ? (
          <Box display="flex" flexDirection="column" gridGap={8}>
            <QuestionSkeleton />
            <EvidencesSkeleton />
          </Box>
        ) : (
          <>
            <ContentHeader
              title={quizQuestionTemplate?.questionnaire?.kind}
              historyBreadcumbs={false}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<HelpCircleIcon size={20} />}
                onClick={handleOpenTutorialDialog}
                fullWidth
              >
                Tutorial
              </Button>
            </ContentHeader>
            <QuizQuestionTemplateForm
              key={quizQuestionTemplateId}
              quizQuestionTemplate={quizQuestionTemplate}
              onSubmit={onSubmit}
              onNavigate={navigateToQuestion}
              redirectToHome={redirectToHome}
              previousQuestion={previousQuestion}
              nextQuestion={nextQuestion}
              isSubmitting={isSubmitting}
            />
          </>
        )}
      </Box>

      <QuestionTutorialDialog
        open={openTutorialDialog}
        setOpen={setOpenTutorialDialog}
        onClose={handleCloseTutorialDialog}
      />
    </Page>
  )
}

export default QuizQuestionTemplateNew
